export interface UserRegionInfo {
  timeZone: string;
  timeZoneAbbr: string;
  regionName: string;
  countryCode?: string;
  latitude?: number;
  longitude?: number;
  isPrecise: boolean;
}

export const REGION_STORAGE_KEY = "bloxida-user-region-v1";

// Maps major timezone prefixes to readable region labels
const TIMEZONE_REGION_MAP: Record<string, string> = {
  "Asia/Kolkata": "India (IST)",
  "Asia/Calcutta": "India (IST)",
  "America/New_York": "New York, USA (EDT/EST)",
  "America/Los_Angeles": "Los Angeles, USA (PDT/PST)",
  "America/Chicago": "Chicago, USA (CDT/CST)",
  "America/Denver": "Denver, USA (MDT/MST)",
  "Europe/London": "London, UK (BST/GMT)",
  "Europe/Paris": "Paris, France (CEST/CET)",
  "Europe/Berlin": "Berlin, Germany (CEST/CET)",
  "Europe/Rome": "Rome, Italy (CEST/CET)",
  "Europe/Madrid": "Madrid, Spain (CEST/CET)",
  "Asia/Tokyo": "Tokyo, Japan (JST)",
  "Asia/Shanghai": "Shanghai, China (CST)",
  "Asia/Singapore": "Singapore (SGT)",
  "Asia/Dubai": "Dubai, UAE (GST)",
  "Asia/Hong_Kong": "Hong Kong (HKT)",
  "Asia/Seoul": "Seoul, South Korea (KST)",
  "Australia/Sydney": "Sydney, Australia (AEST/AEDT)",
  "Australia/Melbourne": "Melbourne, Australia (AEST/AEDT)",
  "America/Sao_Paulo": "São Paulo, Brazil (BRT)",
  "America/Toronto": "Toronto, Canada (EDT/EST)",
  "America/Vancouver": "Vancouver, Canada (PDT/PST)",
  "Africa/Cairo": "Cairo, Egypt (EEST)",
  "Africa/Johannesburg": "Johannesburg, South Africa (SAST)",
  "Europe/Amsterdam": "Amsterdam, Netherlands (CEST/CET)",
  "Europe/Stockholm": "Stockholm, Sweden (CEST/CET)",
  "Europe/Zurich": "Zurich, Switzerland (CEST/CET)",
  "Asia/Bangkok": "Bangkok, Thailand (ICT)",
  "Asia/Jakarta": "Jakarta, Indonesia (WIB)",
};

/**
 * Detect client timezone and infer regional name
 */
export function detectUserRegion(): UserRegionInfo {
  if (typeof window === "undefined") {
    return {
      timeZone: "UTC",
      timeZoneAbbr: "UTC",
      regionName: "Universal (UTC)",
      isPrecise: false,
    };
  }

  // Check cached region preferences
  try {
    const cached = localStorage.getItem(REGION_STORAGE_KEY);
    if (cached) {
      const parsed = JSON.parse(cached);
      if (parsed.timeZone) return parsed;
    }
  } catch {
    // Ignore parse errors
  }

  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  
  // Derive timezone abbreviation (e.g. EST, IST, GMT, PST)
  let timeZoneAbbr = "UTC";
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone,
      timeZoneName: "short",
    }).formatToParts(new Date());
    const tzPart = parts.find((p) => p.type === "timeZoneName");
    if (tzPart) timeZoneAbbr = tzPart.value;
  } catch {
    timeZoneAbbr = timeZone.split("/").pop()?.replace(/_/g, " ") || "Local";
  }

  // Determine readable region name
  let regionName = TIMEZONE_REGION_MAP[timeZone];
  if (!regionName) {
    const city = timeZone.split("/").pop()?.replace(/_/g, " ") || timeZone;
    regionName = `${city} (${timeZoneAbbr})`;
  }

  const info: UserRegionInfo = {
    timeZone,
    timeZoneAbbr,
    regionName,
    isPrecise: false,
  };

  try {
    localStorage.setItem(REGION_STORAGE_KEY, JSON.stringify(info));
  } catch {
    // Ignore storage errors
  }

  return info;
}

/**
 * Format current date/time to region's actual 12-hour format with AM/PM
 */
export function formatRegionTime(
  date: Date = new Date(),
  timeZone?: string,
  includeSeconds: boolean = false,
): string {
  try {
    return new Intl.DateTimeFormat("en-US", {
      timeZone: timeZone || undefined,
      hour: "numeric",
      minute: "2-digit",
      ...(includeSeconds ? { second: "2-digit" } : {}),
      hour12: true,
    }).format(date);
  } catch {
    // Fallback standard formatting
    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      ...(includeSeconds ? { second: "2-digit" } : {}),
      hour12: true,
    });
  }
}

/**
 * Format date for localized display (e.g., "Tuesday, Aug 25, 2026")
 */
export function formatRegionDate(
  date: Date = new Date(),
  timeZone?: string,
): string {
  try {
    return new Intl.DateTimeFormat("en-US", {
      timeZone: timeZone || undefined,
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    }).format(date);
  } catch {
    return date.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }
}
